// src/lib/canApprove.js
//
// ONE IMPORT, ADDED AT 79.12 FOR #531. isBranchManager() is #478's single
// definition of "runs the branch", and writing a fourth inline copy of
// `manager_scope === 'branch'` is exactly what locked rule 8 forbids.
// permissions.js imports nothing, so this is a one-way edge and no cycle.
// Who may approve a material request. Issue #396, Session 78n4, Approvals Gate.
//
// This file answers a PER-RECORD question — "may this person approve THIS
// request" — which is why it exists separately from permissions.js. No predicate
// in that file accepts the record being acted on. Sanctioned split, named in
// CLAUDE.md's locked rules and in permissions.js's own canGovernStations comment.
//
// ── What this used to be, and why it changed ──
//
// Until this session the whole rule was nine lines: admin or executive approves
// anything, and a `manager` approves anything whose branch matches their own.
// No department test, no owner, no capability.
//
// That meant Dallas had four people who could approve every request and no way
// to say which of them should — and one of them, the 811 manager, holds the
// `manager` role ONLY because the Tools module requires it. Approving company
// spending was arriving as a side effect of being able to inspect a forklift.
// That is a segregation-of-duties gap, the same class as #289.
//
// ── The rule now ──
//
// Exactly TWO people are in the approver set for any request:
//
//   1. THE BRANCH MANAGER, always. Never switchable, and this is the failure
//      mode the design is built around rather than an afterthought — if routing
//      sent a request only to a department manager, a department with nobody
//      assigned would produce requests NOBODY can approve, sitting forever with
//      no error and no notification. Keeping the branch manager in the set makes
//      that impossible by construction.
//
//   2. THE MANAGER OF THE DEPARTMENT THE REQUEST CAME FROM, if that person has
//      been granted the approval capability. Routing by department is Oracle's
//      and SAP's model — both route by cost centre, and department is D&R's.
//
// First one to act wins. That is the standard primary-plus-backup pattern.
//
// A manager from a DIFFERENT department cannot approve, even holding the
// capability. Colby's ruling at 78n4, against the gate's own recommendation,
// and the reason is accountability rather than convenience: the branch manager
// holds middle management to account, and that only means something if the
// approval actually had to come from one of the two people who own it.
//
// ── Two deliberate fail-closed choices ──
//
// `manager_scope === 'branch'` grants approval WITHOUT consulting the capability
// switch. The switch cannot lock a branch manager out of their own branch, even
// if somebody sets the column wrong by hand.
//
// Everything else requires `can_approve_requests === true` — written as `=== true`,
// not `!== false`, for the reason CLAUDE.md names: `undefined?.field !== false`
// evaluates to TRUE, so a profile that has not finished loading would read as
// permitted. On an authority check that is the only correct direction to fail.
//
// ── The department comparison is by id, not by name ──
//
// Both sides are `department_id`, a real reference to the departments table.
// A previous version of this design would have compared department NAMES, which
// is how the rest of the app still links a person to a department — and it is
// why renaming a department has to sweep four tables by text match. Routing
// authority across a matched string inherits every one of those failures
// silently. #272, and the migration converts the link before this reads it.
// The rule itself, for one manager against one request. Everything else in this
// file and in the My requests screen goes through here, so there is exactly one
// place the approver rule is written down.
//
// Admin and executive are deliberately NOT handled here: they approve anything
// anywhere, which is a different statement, and folding it in would make them
// appear in the "who is this waiting on" line for every branch in the company.
function isManagerApproverFor(user, request) {
  if (!user || !request) return false;
  if (user.role !== 'manager') return false;

  // Wrong branch ends it regardless of anything else.
  if (String(request.branch_id) !== String(user.branch_id)) return false;

  // The branch manager, always, and not switchable.
  if (user.manager_scope === 'branch') return true;

  // From here, an explicit grant is required. `=== true` rather than
  // `!== false`, so a half-loaded profile fails closed.
  if (user.can_approve_requests !== true) return false;

  // A request carrying no department is one whose requester has no department
  // set. Only the branch manager handles those — there is no department manager
  // to route it to, and guessing one would be exactly the inference #272 exists
  // to remove.
  if (request.department_id == null) return false;

  return String(request.department_id) === String(user.department_id);
}

import { isBranchManager } from './permissions';

export function canApprove(profile, request) {
  if (!profile || !request) return false;
  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  return isManagerApproverFor(profile, request);
}

// ── Transfers. Issues #371 and #425, Session 79.6, gate decisions D3 and D14 ──
//
// READ THIS BEFORE REACHING FOR canApprove() ABOVE.
//
// A transfer handed to canApprove() returns FALSE FOR EVERY MANAGER and TRUE
// for admin and executive, with no error and nothing in the logs. The sending
// branch's manager would see the card with no Approve button and no explanation.
//
// The mechanism is entirely ordinary and that is what makes it dangerous:
// isManagerApproverFor() reads `request.branch_id`, and a transfer has no such
// column — it has from_branch_id and to_branch_id. String(undefined) is
// "undefined", which matches nobody's branch, so the wrong-branch guard ends it
// on the first test. Admin and executive never reach that line at all.
//
// The gate's own words: revision 1 described this file as a nine-line rule and
// said to copy it. #396 REWROTE IT at 78n4 and it is not that any more —
// copying its body would work by accident and read a department a transfer does
// not have. Hence a separate predicate rather than a widened one.
//
// ── The rule ──
//
// THE SENDING BRANCH'S MANAGER, because the sending branch is the one giving
// something up. Colby at 78f. Not purchasing, not accounts payable, and not the
// receiving branch — a branch cannot approve its own supply.
//
// Branch scope only, deliberately, and this is the difference from a material
// request rather than an oversight. A material request routes on the
// REQUESTER'S department, which is a fact about the person asking. A transfer
// has no department at all: it is one branch asking another for material, and
// there is nothing to route on. So it resolves to the branch manager alone,
// which is the same answer canApprove() gives a request whose department is
// null — and correct for the same reason, rather than by coincidence.
//
// ══ AMENDED AT SESSION 79.10 — THERE ARE NOW TWO STAGES ═════════════════════
//
// Colby, 79.10: "a transfer request should run through the same approval
// process that a normal material request runs through — when a team member
// submits it the request lands in the requesting branch's department and branch
// manager request inbox for approval before it's passed on to houston."
//
// That OVERTURNS the single-approval design above, which was his own D3 at 78f,
// and the reasoning is worth keeping because D3 was not wrong — it was
// incomplete. "The sending branch gives something up, so the sending branch
// decides" is still true about SUPPLY. What it did not cover is that the ASKING
// branch gives something up too: a team member could commit another branch's
// shop floor to a week of work with nobody at their own branch seeing it.
//
// The two stages are two different questions and they reach two different sets
// of people:
//
//   `requested`             MAY WE ASK? The REQUESTING branch's department or
//                           branch manager — the same rule a material request
//                           has used since #396, which is the point: a team
//                           member should not have to learn two approval
//                           chains for two ways of getting the same material.
//
//   `awaiting_confirmation` CAN WE SUPPLY? The SENDING branch, and since 79.10
//                           that is the branch manager OR the fabrication
//                           manager. Colby: "the fabrication manager knows the
//                           queue and the lead time, so the date is likelier to
//                           be real." Gate D27 left this open on purpose,
//                           pending real use; this is that answer.
//
// ANY OTHER STATUS IS FALSE. An approved or shipped transfer is nobody's to
// approve, and returning true would put a live button on a finished document.
// THE ASKING BRANCH'S APPROVER TEST, EXTRACTED AT 79.15i SO THERE IS ONE OF IT.
//
// This was the body of canApproveTransfer()'s stage 1. #567 needs the SAME
// question asked at a different moment — who may decide what happens to a line
// the supplying branch refused — and that moment is AFTER the status has moved
// on, so canApproveTransfer() itself cannot answer it: it returns false for
// every status except `requested` and `awaiting_confirmation`.
//
// The gate's B4 said routing reuses canApproveTransfer "existing, unchanged",
// and it cannot, for that reason. Its REASONING still governs and is why this is
// an extraction rather than a second predicate: "a second predicate that must
// agree with the first is a second rule that can disagree with it." One rule,
// two callers.
//
// A branch manager always; a department manager only for their own people.
// `requested_by_department_id` is stamped at creation for exactly this — a
// transfer has no department of its own, so the requester's is carried.
function asksFromMyBranch(profile, transfer) {
  if (transfer.to_branch_id == null) return false;
  if (String(transfer.to_branch_id) !== String(profile.branch_id)) return false;
  if (profile.manager_scope === 'branch') return true;
  if (profile.manager_scope !== 'department') return false;
  // #583, Session 79.21b -- THE APPROVAL SWITCH, as a material request needs it
  // (#396, isManagerApproverFor above). #528's comment said stage one was "the
  // same rule a material request uses" and it was not: it never read the
  // switch, so a manager who holds the role only for the Tools module could
  // approve their team's transfers and route a refusal. `=== true`, so a
  // half-loaded profile fails closed. The bell (getRequestApproverIds) already
  // required the switch - now the button agrees with it.
  if (profile.can_approve_requests !== true) return false;
  if (transfer.requested_by_department_id == null) return false;
  return String(profile.department_id) === String(transfer.requested_by_department_id);
}

// ── THE SUPPLYING BRANCH'S CONFIRMER — gate 371 D3a, extracted at 79.21b ────
//
// Stage 2's rule, pulled out of canApproveTransfer() so the two things that need
// it cannot disagree (gate 553 B4): confirming a request, and raising a request
// for the branch's own shelf ALREADY confirmed (D23). The second has no
// transfer row yet, so this takes anything shaped like one - the supplying
// branch and, on a make request, the manufacturing department.
//
// The branch manager, or the manager of the department that makes it. Status is
// NOT read here: that is the caller's question.
export function canConfirmSupply(profile, transferLike) {
  if (!profile || !transferLike) return false;
  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (role !== 'manager') return false;
  if (transferLike.from_branch_id == null) return false;
  if (String(transferLike.from_branch_id) !== String(profile.branch_id)) return false;
  // `=== 'branch'` rather than `!== 'department'`: a half-loaded profile must
  // fail closed on an authority check.
  if (profile.manager_scope === 'branch') return true;
  // The fabrication manager, and ONLY for the work they actually run. A
  // department manager at the sending branch who runs something else is not
  // the person who knows the lead time.
  if (profile.manager_scope !== 'department') return false;
  if (transferLike.manufacturing_department_id == null) return false;
  return String(profile.department_id) === String(transferLike.manufacturing_department_id);
}

// ── WHO MAY TAKE A TRANSFER BACK — #590, gate 371 D31, Session 79.21b ───────
//
// The person who asked; the managers who may approve their requests (the
// first-stage rule, switch and all); admins and executives. NEVER the supplying
// branch - it has its own two exits, and a colleague's request is not theirs to
// end. For a request the branch made for its OWN shelf the asking and supplying
// branch are the same, so it is the asker and the people who may confirm it
// (gate 553 B4).
//
// Only while the request is live. What the button offers - the whole request or
// "we don't need the rest" - is the screen's question, answered from what has
// been loaded; the database refuses the wrong one either way.
export function canWithdrawTransfer(profile, transfer) {
  if (!profile || !transfer) return false;
  if (!['requested', 'awaiting_confirmation', 'approved', 'in_transit'].includes(transfer.status)) return false;
  if (transfer.requested_by != null && String(transfer.requested_by) === String(profile.id)) return true;
  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (role !== 'manager') return false;
  const ownShelf = transfer.from_branch_id != null
    && String(transfer.from_branch_id) === String(transfer.to_branch_id);
  if (ownShelf) return canConfirmSupply(profile, transfer);
  return asksFromMyBranch(profile, transfer);
}

// ── WHO MAY TAKE A PURCHASE REQUEST BACK — #590, gate 391 D13-D14 ──────────
//
// ONLY THE PERSON WHO ASKED, and only until Purchasing places the order.
// Managers already have Deny before approval and Purchasing has Cancel after;
// a third door beside those would be two answers to one question. Differs from
// the transfer rule on purpose (D13).
export function canWithdrawMaterialRequest(profile, request) {
  if (!profile || !request) return false;
  if (request.requested_by == null || String(request.requested_by) !== String(profile.id)) return false;
  return ['pending', 'approved', 'pending_purchase_approval'].includes(request.status);
}

// #567 — who decides what happens to the lines that were refused.
//
// The same people who could have approved the original ask, which is #565's D5
// and D6: the decision comes home to the ASKING branch. Deliberately NOT gated
// on status, because by the time there is anything to route the transfer is
// `approved` (some lines accepted) or `denied` (none were).
//
// Asked as *may this person decide*, never as *is there something to decide* —
// #530's whole argument, where two money figures were guarded on "is it priced"
// and looked compliant only because the test data had no costs in it. Whether
// any line is actually refused is the SCREEN's question, asked separately.
export function canRouteRefusedLines(profile, transfer) {
  if (!profile || !transfer) return false;
  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (role !== 'manager') return false;
  return asksFromMyBranch(profile, transfer);
}

export function canApproveTransfer(profile, transfer) {
  if (!profile || !transfer) return false;

  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (role !== 'manager') return false;

  // ── Stage 1 — the branch that is ASKING ──────────────────────────────────
  //
  // to_branch_id, which is the exact read D3's comment used to forbid. It is
  // correct HERE and would still be wrong at stage 2: a branch may agree that
  // its own people should ask, and may never agree to its own supply.
  if (transfer.status === 'requested') return asksFromMyBranch(profile, transfer);

  // ── Stage 2 — the branch that would SUPPLY ───────────────────────────────
  // One rule with the own-shelf "raise it already confirmed" check, since
  // 79.21b - canConfirmSupply() above carries the reasoning.
  if (transfer.status === 'awaiting_confirmation') return canConfirmSupply(profile, transfer);

  return false;
}

// Who a request is waiting on, given a list of managers to consider — used by
// My requests to name a real person instead of a branch. #391 D5, restored here
// because #396 is what finally makes the name TRUE rather than arbitrary.
//
// Returns the department manager FIRST when there is one, because that is the
// person the request is routed to; the branch manager follows as the backstop
// who can also act. Callers that want one name take the first.
//
// This shares `isManagerApproverFor` with canApprove() rather than restating
// the rule, which is the point: if the two ever disagree, a requester is told
// to chase somebody who has no Approve button.
export function approversFor(managers, request) {
  if (!request) return [];
  return (managers || [])
    .filter(m => m.is_active !== false && isManagerApproverFor(m, request))
    .sort((a, b) => {
      const rank = m => (m.manager_scope === 'department' ? 0 : 1);
      return rank(a) - rank(b);
    });
}

// #643, Session 79.22b.1 -- EVERY PERSON WHO CAN ACT, NOT THE FIRST ROW A
// QUERY HAPPENED TO RETURN.
//
// `approversFor` ranks department scope ahead of branch scope and no further,
// so among two equally-ranked department managers the winner was whatever order
// the database returned. That is not stable: an unrelated edit to one manager's
// profile rewrites the row, and the same request named a different colleague an
// hour later with nothing about the request having changed. "With Adrian Cole
// since Sunday" also reads as an accusation when two people are equally
// responsible -- and when the request was withdrawn, all three were told, so the
// app's behaviour was already wider than the sentence it showed.
//
// The ORDER is fixed at the query (`.order('full_name')`). These two say the
// true thing once it is.
//
// EVERYBODY AT THE TOP RANK, and the backstop is still not named. The branch
// manager can act too, but the department managers are who the request is
// ROUTED to, and naming the backstop beside them would tell the asker to chase
// somebody the request was never sent to. That is the rule #396 established and
// this does not change it.
export function topApprovers(managers, request) {
  const all = approversFor(managers, request);
  if (all.length === 0) return [];
  const scope = all[0].manager_scope;
  return all.filter(a => a.manager_scope === scope);
}

// THE JOINING WORD CARRIES A DIFFERENT FACT IN EACH SENTENCE, which is why it
// is the caller's choice rather than a constant.
//
// "With Adrian Cole OR Test Department Manager" -- EITHER can approve it, and
// that is what the waiting line means.
// "Adrian Cole AND Test Department Manager will be told" -- BOTH are, and that
// is what the withdraw sheet means. `withdrawMaterialRequest` notifies the
// whole approver set, read in materialRequests.js rather than assumed.
//
// Getting this backwards understates the second one, which is the half a person
// would act on.
export function approverPhrase(approvers, { join = 'or' } = {}) {
  const names = (approvers || []).map(a => a && a.full_name).filter(Boolean);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} ${join} ${names[names.length - 1]}`;
}

// "Say why, so X knows." -- AND X IS SOMETIMES PLURAL, WHICH IT ALREADY WAS
// BEFORE #643 TOUCHED ANYTHING. Two live sentences read "so Purchasing and the
// executives knows" and "so the Dallas managers knows"; a transfer's read "so
// Dallas's approvers knows". Found doing what #643's own regression surface
// asks -- re-read all three strings for grammar - so it is folded in here
// rather than logged separately.
//
// "OR" TAKES THE SINGULAR and that is not an oversight: the verb agrees with
// the nearer subject, so "so Adrian Cole or Dana Ruiz knows" is correct. Only
// "and", and the collective plurals, take "know".
//
// The pattern is branchTransfers.js's own, which picks has/have for the same
// phrases, rather than a second list that can drift from it.
export function knowsVerb(who) {
  return /( and |approvers$|managers$|executives$)/.test(String(who || '')) ? 'know' : 'knows';
}

// ── WHO MAY DECLARE THE BALANCE LOST — #531 part 2, Session 79.12 ───────────
//
// Colby's ruling at 79.12, and it is a WIDENING on branch with a NARROWING on
// role. Both halves matter and both were researched before anything was built.
//
// WIDER ON BRANCH. Until now only the RECEIVING branch could close a transfer
// short, which produces an absurd case: Houston must ask Dallas for permission
// to write off frames sitting on Houston's own rack. Every enterprise system
// studied settles a shortfall as an ADJUSTMENT rather than as a transfer
// action, and an adjustment can be raised by either side — SAP says in as many
// words that which plant books the loss is a business decision, and NetSuite,
// Dynamics 365 and Oracle Fusion all receive what arrived and book the rest
// separately. Fishbowl, the direct mid-market match, has Stede's exact Close
// Short control. Restricting it to the receiver was STRICTER THAN SAP.
//
// NARROWER ON ROLE, and this is the half that takes something away. Anyone at
// the receiving branch could close short before, team member included. Counting
// what came off a truck and declaring stock lost are different acts: the first
// needs the person standing at the pallet, the second writes off money. So
// counting stays exactly where it was — `isReceivable(transfer) &&
// atReceivingBranch`, unchanged — and this answers only the second question.
//
// ** THE STANDARDS FLOOR THIS NARROWING TOUCHES IS #492: A CONTROL THAT
// REFUSES A PRESS MUST ANSWER IT. ** It still holds, and not by accident — a
// team member at the receiving branch keeps the Receive button and can record
// a short count there, so the path is not blocked, and the sheet names who can
// write the balance off rather than leaving an absence to be puzzled over.
// Nothing here is a dead button; the control is absent, and the reason is on
// the screen beside it.
//
// WHAT IS ALREADY RECORDED, so nobody builds an audit trail that exists:
// close_branch_transfer_short() refuses an empty reason, stamps `closed_by`
// and `closed_at`, takes the row FOR UPDATE so it cannot run twice, and
// closeTransferShort() tells both branches' branch managers and the asking
// branch's approving managers -- by bell AND email, one list, no admins since
// #683 (Colby's ruling; it was every admin as well until 79.22b.4.3) -- with
// the dollar value attached and no preference toggle. That notification is a
// STRONGER control than a permission gate, which is the argument for widening
// rather than against it: a gate is satisfied by asking a colleague to press
// the button, and the notification cannot be turned off and names the money.
//
// isBranchManager() rather than a fourth inline copy of `manager_scope ===
// 'branch'` — #478's single definition, and locked rule 8. It is called with a
// synthesized flag because this file works from `profile` like its neighbour
// above rather than from the auth object.
//
// SCOPE, STATED PLAINLY BECAUSE IT IS NOT A GUARANTEE: this is the React
// layer. branch_transfers' UPDATE policy is USING (true) WITH CHECK (true) and
// close_branch_transfer_short() checks the reason, the row and the status but
// nothing about the caller — so the old receiving-branch rule was advisory too.
// Widening here weakens no enforcement because there was none to weaken. The
// server-side pass is the JWT and row-security sprint, Sep 22-26, and gate D21
// already puts transfer authority in its scope.
export function canCloseTransferShort(profile, transfer) {
  if (!profile || !transfer) return false;

  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (role !== 'manager') return false;

  // `=== 'branch'` inside isBranchManager, so a half-loaded profile fails
  // closed on an authority check — the same reasoning as stage 2 above.
  //
  // ** THE ROLE FLAG IS DERIVED RATHER THAN HARDCODED `true`, AND THE FIRST
  // VERSION HARDCODED IT. ** That made isBranchManager()'s own role test a
  // no-op, so the early return above was the ONLY thing keeping a team
  // member out — and deleting that one line was proven to change nothing
  // any test could see, because no fixture carried a team member with
  // manager_scope set. Passing the real flag makes both checks load-bearing
  // and the early return merely an early return.
  if (!isBranchManager({ isManager: role === 'manager', profile })) return false;

  // EITHER END. The sending branch is the one that can see the rack the
  // material is not on, and the receiving branch is the one that counted it
  // in. A hub in the middle is neither: nobody is employed at Austin, so a
  // manager there has no part in this decision.
  return String(transfer.to_branch_id) === String(profile.branch_id)
      || String(transfer.from_branch_id) === String(profile.branch_id);
}

// ── WHO MAY SAY "NOTHING MORE IS COMING" — #616, Session 79.22b ─────────────
//
// It is the SENDING branch's statement about what it will never send (gate
// 517 D56), so it belongs to whoever is at that branch - any role, because
// the load panel it sits in has always been offered to whoever loads there -
// plus admin and executive, who decide anywhere.
//
// NARROWED FROM WHAT THE SCREEN DID. The panel also appeared at a hub on the
// way, where the goods only stand: San Antonio could have told Dallas that
// Houston would never send the rest, which San Antonio cannot know. The
// database now refuses that press (transfer_rule_allows()), so the screen
// stops offering it rather than drawing a button that is refused - #492.
//
// The database's copy of this rule is held to the same answers by
// transferCallerRules.test.js.
export function canSayNothingMoreComing(profile, transfer) {
  if (!profile || !transfer) return false;
  const role = profile.role;
  if (role === 'admin' || role === 'executive') return true;
  if (transfer.from_branch_id == null || profile.branch_id == null) return false;
  return String(transfer.from_branch_id) === String(profile.branch_id);
}
